import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import Typhography from '../../../shared/Typhography/Typhography';
import styles from './PhaseDistributionChart.module.scss';
import type { IPhaseAggregate } from '../../../models/trainingStatistics';

/**
 * Sezione 2 — Distribuzione per Fase Seduta (Pie chart).
 * Spec: LEOPART_ProgrammazioneCampo_Spec_v1.1.1.md righe 2789-2825.
 */
interface PhaseDistributionChartProps {
  data: IPhaseAggregate[];
  phaseLabels?: Record<string, string>;
}

const COLORS = [
  '#063444', '#0f5c7a', '#1a8fb0', '#ffd15a', '#f59e0b',
  '#10b981', '#8b5cf6', '#ef4444', '#6366f1', '#ec4899',
];

const fmtMinutes = (m: number) => {
  const h = Math.floor(m / 60);
  const min = m % 60;
  return h > 0 ? `${h}h ${min}m` : `${min}m`;
};

const PhaseDistributionChart: React.FC<PhaseDistributionChartProps> = ({
  data,
  phaseLabels = {},
}) => {
  if (!data || data.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Typhography size="regular-14" className={styles.emptyText}>
          Nessuna classificazione disponibile per il periodo selezionato.
        </Typhography>
      </div>
    );
  }

  const chartData = data.map((d) => ({
    name: phaseLabels[d.phaseId] ?? d.phaseId,
    value: d.minutes,
    percentage: d.percentage,
  }));

  return (
    <div className={styles.container}>
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={100}
            paddingAngle={3}
            dataKey="value"
          >
            {chartData.map((_, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value, name) => [
              `${fmtMinutes(Number(value))} (${chartData.find((d) => d.name === name)?.percentage ?? 0}%)`,
              name,
            ]}
          />
          <Legend iconType="circle" iconSize={10} />
        </PieChart>
      </ResponsiveContainer>

      <div className={styles.table}>
        <div className={styles.tableHeader}>
          <span>Fase</span>
          <span>Minuti</span>
          <span>%</span>
        </div>
        {data.map((d, i) => (
          <div key={d.phaseId} className={styles.tableRow}>
            <div className={styles.colorDot} style={{ background: COLORS[i % COLORS.length] }} />
            <span className={styles.phaseName}>{phaseLabels[d.phaseId] ?? d.phaseId}</span>
            <span className={styles.phaseMinutes}>{fmtMinutes(d.minutes)}</span>
            <span className={styles.phasePercent}>{d.percentage}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PhaseDistributionChart;
